import React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { Chip, StatusGlyph } from "@outpave/ds";
import type { ChipTone } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  BankIcon,
  CreditCardIcon,
  Exchange01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import moment from "moment";
import { GET_PAYMENT_LINK_PAYMENTS } from "graphql/paymentLink";
import { formatCashFull } from "utils/formatCash";
import { PaymentLinkPaymentStatus } from "enums/PaymentLink";

const BANK_METHODS = ["us_bank_account", "ach_debit", "bank_transfer"];

interface PaymentRow {
  id: string;
  status: PaymentLinkPaymentStatus;
  payerEmail: string;
  payerName?: string;
  paymentMethodType?: string;
  paymentMethodBrand?: string;
  paymentMethodLast4?: string;
  totalCharged: number;
  currency: string;
  createdAt?: number;
}

interface PaymentLinkPaymentRowsProps {
  paymentLinkId: string;
  limit?: number;
  onViewAll: () => void;
}

const PaymentLinkPaymentRows: React.FC<PaymentLinkPaymentRowsProps> = ({
  paymentLinkId,
  limit = 5,
  onViewAll,
}) => {
  const { t } = useTranslation();

  const { data, loading } = useQuery(GET_PAYMENT_LINK_PAYMENTS, {
    variables: { paymentLinkId, first: limit },
    fetchPolicy: "network-only",
  });

  const payments: PaymentRow[] =
    data?.paymentLinkPayments?.edges?.map(
      (edge: { node: PaymentRow }) => edge.node,
    ) || [];

  if (!loading && !payments.length) {
    return (
      <Typography variant="body2" color="var(--color-text-tertiary)">
        {t("No payments yet")}
      </Typography>
    );
  }

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}
    >
      {payments.map((payment) => {
        const { tone, glyph, name } = presentation(payment.status);

        return (
          <Box
            key={payment.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-3)",
              paddingY: "var(--space-2)",
            }}
          >
            <HugeiconsIcon icon={methodIcon(payment)} size={20} />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap>
                {payment.payerName || payment.payerEmail}
              </Typography>
              <Typography variant="caption" color="var(--color-text-tertiary)">
                {describeMethod(payment, t)}
              </Typography>
            </Box>

            <Typography variant="subtitle2">
              {formatCashFull(payment.totalCharged, payment.currency)}
            </Typography>

            <Chip
              size="sm"
              tone={tone}
              label={t(name)}
              leading={<StatusGlyph status={glyph} tone={tone} />}
            />
          </Box>
        );
      })}

      <Button
        variant="default-soft"
        fullWidth
        onClick={onViewAll}
        sx={{ marginTop: "var(--space-2)" }}
      >
        {t("View all payments")}
      </Button>
    </Box>
  );
};

function methodIcon(payment: PaymentRow) {
  if (payment.status === PaymentLinkPaymentStatus.REFUNDED)
    return Exchange01Icon;

  return BANK_METHODS.includes(payment.paymentMethodType || "")
    ? BankIcon
    : CreditCardIcon;
}

function describeMethod(
  payment: PaymentRow,
  t: (key: string) => string,
): string {
  const when = payment.createdAt ? moment(payment.createdAt).format("lll") : "";
  const method = payment.paymentMethodLast4
    ? `${payment.paymentMethodBrand || t("Card")} ${payment.paymentMethodLast4}`
    : BANK_METHODS.includes(payment.paymentMethodType || "")
      ? t("Bank account")
      : "";

  return [when, method].filter(Boolean).join(" · ");
}

function presentation(status: PaymentLinkPaymentStatus): {
  tone: ChipTone;
  glyph: string;
  name: string;
} {
  switch (status) {
    case PaymentLinkPaymentStatus.SUCCEEDED:
      return { tone: "positive", glyph: "paid", name: "Paid" };
    case PaymentLinkPaymentStatus.PROCESSING:
      return { tone: "info", glyph: "processing", name: "Processing" };
    case PaymentLinkPaymentStatus.FAILED:
      return { tone: "negative", glyph: "failed", name: "Failed" };
    case PaymentLinkPaymentStatus.REFUNDED:
      return { tone: "neutral", glyph: "refunded", name: "Refunded" };
    case PaymentLinkPaymentStatus.PARTIALLY_REFUNDED:
      return { tone: "neutral", glyph: "refunded", name: "Partially Refunded" };
    case PaymentLinkPaymentStatus.CANCELED:
      return { tone: "neutral", glyph: "canceled", name: "Canceled" };
    default:
      return { tone: "neutral", glyph: "pending", name: "Pending" };
  }
}

export default PaymentLinkPaymentRows;
